import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import AccountModal from "@/components/AccountModal";
import { LogOut, ArrowLeft, Settings, ShieldAlert } from "lucide-react";

const participantNav = [
  { to: "/studio", label: "Journey" },
  { to: "/sessions", label: "Sessions" },
  { to: "/milestones", label: "Milestones" },
  { to: "/measures", label: "Progress" },
  { to: "/deliverables", label: "Deliverables" },
  { to: "/messages", label: "Messages" },
  { to: "/export", label: "Export" },
  { to: "/intake", label: "Intake" },
];

export default function Layout({ children, coachContext }) {
  const { user, logout, activeParticipant, setActiveParticipant } = useAuth();
  const navigate = useNavigate();
  const [showAccount, setShowAccount] = useState(false);
  const notif = useNotifications();

  const isCoach = user?.role === "coach";
  const unreadMessages = !isCoach ? (notif?.unread_messages || 0) : 0;

  return (
    <div className="min-h-screen bg-brand-linen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-brand-linen/80 border-b border-stone-200">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="flex h-16 items-center justify-between">
            <button
              data-testid="brand-home-btn"
              onClick={() => navigate(isCoach ? "/coach" : "/studio")}
              className="flex items-center gap-3 group"
            >
              <span className="block h-px w-8 bg-brand-primary transition-all duration-300 group-hover:w-12" />
              <span className="font-serif text-2xl tracking-tight text-stone-900">Building The Bridge</span>
            </button>

            {!isCoach && (
              <nav className="hidden md:flex items-center gap-7">
                {participantNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `relative text-sm transition-colors duration-300 ${isActive ? "text-brand-primary" : "text-stone-500 hover:text-stone-900"}`
                    }
                  >
                    {n.label}
                    {n.to === "/messages" && unreadMessages > 0 && (
                      <span data-testid="nav-messages-badge" className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-primary px-1 text-[10px] font-medium text-white">
                        {unreadMessages}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            )}

            <div className="flex items-center gap-4">
              {isCoach && activeParticipant && (
                <button
                  data-testid="coach-back-btn"
                  onClick={() => { setActiveParticipant(null); navigate("/coach"); }}
                  className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors"
                >
                  <ArrowLeft size={15} /> All participants
                </button>
              )}
              <span className="hidden sm:inline text-xs text-stone-500">
                {user?.name} · {isCoach ? "Coach" : "Participant"}
              </span>
              <button
                data-testid="account-btn"
                onClick={() => setShowAccount(true)}
                title="Account & password"
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors"
              >
                <Settings size={15} /> <span className="hidden sm:inline">Account</span>
              </button>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors"
              >
                <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {isCoach && activeParticipant && coachContext && (
        <div className="border-b border-stone-200 bg-brand-parchment/50">
          <div className="mx-auto max-w-6xl px-6 lg:px-10 py-2.5 text-sm text-stone-600">
            Viewing <span className="text-stone-900 font-medium">{activeParticipant.name}</span>'s studio
          </div>
        </div>
      )}

      {isCoach && user?.must_change_password && (
        <div data-testid="default-password-nudge" className="border-b border-brand-accent/40 bg-brand-accent/10">
          <div className="mx-auto max-w-6xl px-6 lg:px-10 py-3 flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-sm text-stone-700">
              <ShieldAlert size={16} className="text-brand-accent" />
              You're still using the default password. Set your own before sharing access.
            </span>
            <button
              data-testid="nudge-change-password"
              onClick={() => setShowAccount(true)}
              className="rounded-sm bg-brand-primary px-4 py-1.5 text-sm text-white hover:bg-brand-hover transition-colors"
            >
              Change it now
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-6 lg:px-10 py-10 animate-fade-up">{children}</main>

      <footer className="border-t border-stone-200 mt-16">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 py-8 text-xs text-stone-400">
          A 16-week, coach-led transformation journey · light digital support for deep work.
        </div>
      </footer>

      {showAccount && <AccountModal onClose={() => setShowAccount(false)} />}
    </div>
  );
}
